import type {
  ApiLanguage, CachedLangData, LangPack, LangPackStringValuePlural,
} from '../../api/types';

import readStrings from './readStrings';

const LOCAL_LANG_VERSION = 0;

interface LocalLangMeta {
  name: string;
  nativeName: string;
  pluralCode: string;
  loader: () => Promise<{ default: string }>;
}

const LOCAL_LANGUAGES: Record<string, LocalLangMeta> = {
  'en': {
    name: 'English',
    nativeName: 'English',
    pluralCode: 'en',
    loader: () => import('../../assets/localization/fallback.strings'),
  },
  'zh-hans': {
    name: 'Chinese (Simplified)',
    nativeName: '简体中文',
    pluralCode: 'zh',
    loader: () => import('../../assets/localization/zh-hans.strings'),
  },
  'es': {
    name: 'Spanish',
    nativeName: 'Español',
    pluralCode: 'es',
    loader: () => import('../../assets/localization/es.strings'),
  },
};

export function getAvailableLocalLangCodes(): string[] {
  return Object.keys(LOCAL_LANGUAGES);
}

export function getLocalLanguages(): ApiLanguage[] {
  return Object.entries(LOCAL_LANGUAGES).map(([langCode, meta]) => ({
    langCode,
    name: meta.name,
    nativeName: meta.nativeName,
    pluralCode: meta.pluralCode,
    stringsCount: 0,
    translatedCount: 0,
    translationsUrl: '',
  }));
}

export function isLocalLangAvailable(langCode: string): boolean {
  return langCode in LOCAL_LANGUAGES;
}

export default async function readLocalStrings(langCode: string): Promise<CachedLangData | undefined> {
  const meta = LOCAL_LANGUAGES[langCode];
  if (!meta) return undefined;

  const file = await meta.loader();
  const fileData = file.default;
  const rawStrings = readStrings(fileData);

  const strings: LangPack['strings'] = {};

  const PLURAL_SUFFIXES = new Set(['zero', 'one', 'two', 'few', 'many', 'other']);

  Object.entries(rawStrings).forEach(([key, value]) => {
    const lastUnderscore = key.lastIndexOf('_');
    const possibleSuffix = lastUnderscore > 0 ? key.slice(lastUnderscore + 1) : undefined;
    const isPluralKey = possibleSuffix && PLURAL_SUFFIXES.has(possibleSuffix);

    if (!isPluralKey) {
      strings[key] = value;
      return;
    }

    const clearKey = key.slice(0, lastUnderscore);
    const knownValue = (strings[clearKey] || {}) as LangPackStringValuePlural;
    knownValue[possibleSuffix as keyof LangPackStringValuePlural] = value;
    strings[clearKey] = knownValue;
  });

  const langPack: LangPack = {
    langCode,
    version: LOCAL_LANG_VERSION,
    strings,
  };

  const stringsCount = Object.keys(strings).length;

  const language: ApiLanguage = {
    langCode,
    name: meta.name,
    nativeName: meta.nativeName,
    pluralCode: meta.pluralCode,
    stringsCount,
    translatedCount: stringsCount,
    translationsUrl: '',
  };

  return {
    langPack,
    language,
  };
}
